<template>
    <div data-testid="cesion-inline-view">
        <!-- Eventos mode -->
        <template v-if="viewMode === 'eventos'">
            <header class="tw:mb-6">
                <p v-if="nombreAbonado" class="tw:text-slate-700 tw:text-sm tw:font-semibold tw:m-0">
                    {{ nombreAbonado }}
                    <span v-if="nAbonado" class="tw:text-slate-400 tw:font-normal">({{ $t('perfil.n_abonado') }}: {{ nAbonado }})</span>
                </p>
                <p class="tw:text-sm tw:text-slate-500">{{ $t('perfil.cesion_selecciona_partido') }}</p>
            </header>

            <EventList
                :eventos="eventos"
                :loading="loadingEventos"
                :error="errorEventos"
                :selection-loading="loadingEvento"
                :selection-error="errorEvento"
                :selection-error-evento-id="selectionErrorEventoId"
                :resolving-evento-id="resolvingEventoId"
                :expanded-evento-id="expandedEventoId"
                :expanded-sessions="expandedSessions"
                @select-evento="onSelectEvento"
                @select-session="onSelectSession"
                @retry="loadEventos"
            />
        </template>

        <!-- Detalle mode -->
        <template v-else-if="viewMode === 'detalle'">
            <EventDetail
                :evento="eventoInfo"
                :estado="estadoActual"
                :loading="loadingEstado || loadingEvento"
                :error="errorEstado || errorEvento"
                :loading-operacion="loadingOperacion"
                :error-operacion="errorOperacion"
                @ceder="handleCeder"
                @recuperar="handleRecuperar"
            />
        </template>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onMounted, toRef } from 'vue';
    import EventList from '@/modules/front/perfil/components/cesion/EventList.vue';
    import EventDetail from '@/modules/front/perfil/components/cesion/EventDetail.vue';
    import { useCesionAsiento } from '@/composables/front/perfil/useCesionAsiento';

    const props = defineProps({
        abonoId: {
            type: [String, Number],
            required: true,
        },
        nombreAbonado: {
            type: String,
            default: null,
        },
        nAbonado: {
            type: [String, Number],
            default: null,
        },
    });

    const emit = defineEmits(['close']);

    // ─── Composable ──────────────────────────────────────────────────
    const abonadoIdRef = toRef(props, 'abonoId');
    const {
        eventos,
        loadingEventos,
        errorEventos,
        selectedEvento,
        estadoActual,
        loadingEstado,
        errorEstado,
        eventoInfo,
        loadingEvento,
        errorEvento,
        showSessionPicker,
        eventoWithMultipleSessions,
        errorOperacion,
        loadingOperacion,
        loadEventos,
        onSelectEventoFromList,
        selectSessionFromPicker,
        ceder,
        recuperar,
    } = useCesionAsiento(abonadoIdRef);

    // ─── View mode state machine ─────────────────────────────────────
    const viewMode = ref('eventos');

    // ─── Inline session expansion (mirrors CederAsiento.vue pattern) ──
    const resolvingEventoId = ref(null);
    const expandedEventoId = ref(null);
    const selectionErrorEventoId = ref(null);

    const expandedSessions = computed(() => {
        if (!eventoWithMultipleSessions.value?.sesiones) {
            return [];
        }
        return Array.isArray(eventoWithMultipleSessions.value.sesiones)
            ? eventoWithMultipleSessions.value.sesiones
            : [];
    });

    // ─── Event selection handler (inline expansion, like CederAsiento) ──
    async function onSelectEvento(evento) {
        if (!evento?.id) return;

        // Toggle: if already expanded, collapse it
        if (expandedEventoId.value === evento.id) {
            expandedEventoId.value = null;
            selectionErrorEventoId.value = null;
            return;
        }

        resolvingEventoId.value = evento.id;
        selectionErrorEventoId.value = null;
        expandedEventoId.value = null;

        try {
            await onSelectEventoFromList(evento);

            // After loading, check if multi-session event
            if (showSessionPicker.value) {
                // Multi-session: expand inline (same as old CederAsiento.vue)
                expandedEventoId.value = evento.id;
                return;
            }

            // Single-session was auto-selected → watch(selectedEvento) transitions to detalle
            if (errorEvento.value) {
                selectionErrorEventoId.value = evento.id;
            }
        } finally {
            resolvingEventoId.value = null;
        }
    }

    // ─── Session selection from inline expanded list ──────────────────
    async function onSelectSession(sesionId) {
        const currentEventoId = expandedEventoId.value;

        resolvingEventoId.value = currentEventoId;
        expandedEventoId.value = null;
        selectionErrorEventoId.value = null;

        try {
            await selectSessionFromPicker(sesionId);

            if (errorEvento.value) {
                selectionErrorEventoId.value = currentEventoId;
            }
        } finally {
            resolvingEventoId.value = null;
        }
    }

    // ─── Watch selectedEvento → transition to detalle ───────────────
    watch(selectedEvento, newValue => {
        if (newValue) {
            viewMode.value = 'detalle';
        }
    });

    // ─── Ceder / Recuperar handlers ──────────────────────────────────
    async function handleCeder(payload) {
        await ceder(payload);
    }

    async function handleRecuperar() {
        await recuperar();
    }

    // ─── Lifecycle ───────────────────────────────────────────────────
    onMounted(() => {
        loadEventos();
    });
</script>
